<script setup lang="ts">
    import { columns } from '@/tables/columns/transactions';
    import { Combobox, DatePicker } from '@/components/ui/form';
    import { getLocalTimeZone, today } from '@internationalized/date';
    import { Head } from '@inertiajs/vue3';
    import { ref, computed, watch } from 'vue';
    import { router } from '@inertiajs/vue3';
    import { Row } from '@tanstack/table-core';
    import { Separator } from '@/components/ui/separator';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableRefreshButton from '@/components/datatable/DataTableRefreshButton.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import TransferEconomic from '@/components/bank/TransferEconomic.vue';
    import type { SharedData, BreadcrumbItem } from '@/types';

    const props = defineProps<{
        accountsSelect: Record<string, string>[];
        dbAccount: any;
    }>();

    const { hasPermission, hasAnyPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const accountId = computed({
        get: () => page.props.ziggy.query.account_id,
        set: (newAccountId) => {
            router.visit(route('v2.transactions.index', { account_id: newAccountId }), {
                preserveState: true,
            });
        },
    });

    const startDate = ref(today(getLocalTimeZone()).subtract({ months: 1 }));
    const endDate = ref(today(getLocalTimeZone()));
    const amountFrom = ref(undefined);
    const amountTo = ref(undefined);

    watch(selectedCompanyId, (newCompanyId, oldCompanyId) => {
        if (newCompanyId !== oldCompanyId && oldCompanyId !== undefined) {
            router.visit(route('v2.bank.index'));
        }
    });

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.bank',
            href: route('v2.bank.index'),
        },
        {
            title: 'viia.transactions.page_header',
            href: route('v2.transactions.index'),
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.TRANSACTIONS, selectedCompanyId, accountId],
        queryFn: getBank,
        refetchOnWindowFocus: false,
    });

    async function getBank() {
        const { data } = await axios.get(route('v2.transactions.all', selectedCompanyId.value), {
            params: {
                account_id: accountId.value,
                transaction_from_date: startDate.value,
                transaction_to_date: endDate.value,
                transaction_from_amount: amountFrom.value,
                transaction_to_amount: amountTo.value,
            },
        });
        return data.data;
    }

    const dataTableRef = ref();

    const selectedRows = computed(() => {
        if (!dataTableRef.value?.table) return [];

        const selectedRowModel = dataTableRef.value.table.getFilteredSelectedRowModel();
        return selectedRowModel.flatRows.map((row: Row<any>) => row.original);
    });

    watch(accountId, (newAccountId, oldAccountId) => {
        if (newAccountId !== oldAccountId && oldAccountId !== undefined) {
            dataTableRef.value?.table.resetRowSelection();
        }
    });

    const canTransferEconomic = computed(() => {
        return hasAnyPermission([
            Permission.BANK_CREATE,
            Permission.BANK_UPDATE,
            Permission.BANK_DELETE,
        ]);
    });
</script>

<template>
    <Head :title="$t('viia.header')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard v-if="hasPermission(Permission.BANK_VIEW) && selectedCompanyId">
            <DataTable
                ref="dataTableRef"
                :table-key="QueryKey.TRANSACTIONS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-sorting="[{ id: 'date', desc: true }]"
                :default-column-visibility="{
                    select: canTransferEconomic,
                }"
            >
                <template #title>
                    <Combobox
                        v-model="accountId"
                        :options="accountsSelect"
                        :show-remove-button="false"
                        content-class="min-w-sm"
                    />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="transactions">
                        <div class="flex items-center gap-1">
                            <span class="mr-2 text-[10px] uppercase">
                                {{ $t('tables.filters.date_from_to') }}
                            </span>
                            <DatePicker v-model="startDate" />
                            <span>-</span>
                            <DatePicker v-model="endDate" />
                        </div>
                        <Separator
                            orientation="vertical"
                            class="hidden h-2.5! md:flex"
                        />
                        <div class="flex items-center gap-1">
                            <span class="mr-2 text-[10px] uppercase">
                                {{ $t('tables.filters.amount_from_to') }}
                            </span>
                            <ThousandSeparatorInput
                                v-model="amountFrom"
                                class="w-28"
                                :placeholder="$t('translations.kr')"
                                with-decimals
                            />
                            <span>-</span>
                            <ThousandSeparatorInput
                                v-model="amountTo"
                                class="w-28"
                                :placeholder="$t('translations.kr')"
                                with-decimals
                            />
                        </div>
                        <Separator
                            orientation="vertical"
                            class="hidden h-2.5! md:flex"
                        />
                        <DataTableRefreshButton
                            @refetch="refetch"
                            :is-fetching="isFetching"
                        />
                        <TransferEconomic
                            v-if="canTransferEconomic"
                            :selected-company-id="selectedCompanyId"
                            :selected-rows="selectedRows"
                            :account-id="accountId"
                            :db-account="dbAccount"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
